import { useEffect, useRef, useState, useMemo } from "react"
import cx from "classnames"
import type { Client } from "viem"
import { Message } from "@locmod/intl"
import { useAccount } from "@azuro-org/sdk-social-aa-connector"
import { openModal } from "@locmod/modal"
import localStorage from "@locmod/local-storage"
import { isUserRejectedRequestError, toLocaleString } from "helpers"
import { waitForTransactionReceipt } from "viem/actions"
import { buttonMessages } from "components/inputs"
import { Icon } from "components/ui"

import messages from "./messages"
import { useClient, useWalletClient, useChainId } from "wagmi"
import { sendGTMEvent } from "@next/third-parties/google"

import { ConnectorName, useWallet } from "wallet"
import { useUserContext } from "src/contexts/user"

import { useDetailedOvertimeBetslip } from "@/contexts/DetailedOvertimeBetslipContext"
import { isSameMarket } from "@/Overtime/utils/marketsV2"
import type {
  SportMarket,
  TicketMarket,
  TicketPosition,
} from "@/Overtime/types/markets"
import useSportsMarketsV2Query from "@/Overtime/queries/useSportsMarketsV2Query"
import { useUserTicketsQuery } from "@/Overtime/queries/useUserTicketsQuery"
import { StatusFilter } from "@/Overtime/enums/markets"
import { useTicket } from "@/contexts/TicketContext"
import { isEqual } from "lodash"
import { getCollateral } from "@/Overtime/utils/collaterals"
import {
  SYSTEM_BET_MINIMUM_DENOMINATOR,
  SYSTEM_BET_MINIMUM_MARKETS,
} from "@/Overtime/constants/markets"
import { useBetPlacement } from "@/Overtime/hooks/useBetPlacement"
import { useAllowance } from "@/Overtime/hooks/useAllowance"
import {
  type SupportedNetwork,
  type NetworkConfig,
} from "@/Overtime/types/network"
import { useBalanceContext } from "src/contexts/BalanceContext"
import updateOvertimeDiscordChannel from "@/helpers/updateOvertimeDiscordChannel"
import useChainProvider from "@/hooks/useChainProvider"

type BetButtonProps = {
  isEnoughBalance: boolean
  isBalanceFetching: boolean
  openMarkets: boolean
}

const OvertimeBetButton: React.FC<BetButtonProps> = ({
  isEnoughBalance,
  isBalanceFetching,
  openMarkets,
}) => {
  const { payout, changeBetAmount } = useDetailedOvertimeBetslip()
  const { connector, account: address } = useWallet()
  const { refetchBalances } = useBalanceContext()
  const [ticketMarkets, setTicketMarkets] = useState<TicketMarket[]>([])
  const [unavailableMarkets, setUnavailableMarkets] = useState<
    TicketPosition[]
  >([]) // TODO
  const previousTicketOdds = useRef<
    { position: number; odd: number; gameId: string; proof: string[] }[]
  >([])

  const { isOvertimeChain } = useChainProvider()
  const systemBetDenominator = +(
    (localStorage.getItem("systemBetDenominator") as string) ||
    SYSTEM_BET_MINIMUM_DENOMINATOR
  )

  const { ticket, state, removeAll } = useTicket()
  const client = useClient()
  const { chain } = useAccount()
  const walletClient = useWalletClient()
  const sportMarketsQuery = useSportsMarketsV2Query(
    StatusFilter.OPEN_MARKETS,
    false,
    { networkId: chain?.id as SupportedNetwork },
    undefined,
    {
      enabled: !!openMarkets,
    }
  )

  // Add user tickets query for fetching after successful bet placement
  const networkConfig: NetworkConfig = {
    networkId: chain?.id as SupportedNetwork,
    client,
  }
  const userTicketsQuery = useUserTicketsQuery(
    address as `0x${string}`,
    networkConfig,
    {
      enabled: false, // We'll manually refetch after bet placement
    }
  )

  const sportMarketsProofsQuery = useSportsMarketsV2Query(
    StatusFilter.OPEN_MARKETS,
    true,
    { networkId: chain?.id as SupportedNetwork },
    ticket,
    {
      enabled: !!ticket.length,
    }
  )
  const sportMarkets = useMemo(() => {
    if (sportMarketsProofsQuery.isSuccess && sportMarketsProofsQuery.data) {
      return sportMarketsProofsQuery.data[StatusFilter.OPEN_MARKETS]
    }
    if (openMarkets) {
      return openMarkets
    }
    if (sportMarketsQuery.isSuccess && sportMarketsQuery.data) {
      return sportMarketsQuery.data[StatusFilter.OPEN_MARKETS]
    }
    return undefined
  }, [
    openMarkets,
    sportMarketsProofsQuery.data,
    sportMarketsProofsQuery.isSuccess,
    sportMarketsQuery.data,
    sportMarketsQuery.isSuccess,
  ])

  useEffect(() => {
    if (sportMarkets && Array.isArray(sportMarkets)) {
      const sportOpenMarkets = sportMarkets.reduce(
        (acc: SportMarket[], market: SportMarket) => {
          acc.push(market)
          market.childMarkets.forEach((childMarket: SportMarket) => {
            acc.push(childMarket)
          })
          return acc
        },
        []
      )

      const openTicketMarkets: TicketMarket[] = ticket
        .filter(ticketPosition =>
          sportOpenMarkets.some(
            (market: SportMarket) =>
              isSameMarket(market, ticketPosition) &&
              market.odds[ticketPosition.position] !== 0
          )
        )
        .map(ticketPosition => {
          const openMarket: SportMarket = sportOpenMarkets.filter(
            (market: SportMarket) => isSameMarket(market, ticketPosition)
          )[0]
          return {
            ...openMarket,
            position: ticketPosition.position,
            odd: openMarket.odds[ticketPosition.position],
          }
        })

      if (ticket.length > openTicketMarkets.length) {
        const notOpenedMarkets = ticket.filter(ticketPosition =>
          openTicketMarkets.every(
            (market: SportMarket) => !isSameMarket(market, ticketPosition)
          )
        )

        setUnavailableMarkets(notOpenedMarkets)
      } else {
        setUnavailableMarkets([])
      }

      const ticketOdds = openTicketMarkets.map(market => ({
        odd: market.odd,
        position: market.position,
        gameId: market.gameId,
        proof: market.proof,
      }))

      if (!isEqual(previousTicketOdds.current, ticketOdds)) {
        setTicketMarkets(openTicketMarkets)
        previousTicketOdds.current = ticketOdds
      }
    }
  }, [sportMarkets, ticket])

  const [wasApprovePending, setWasApprovePending] = useState(false)
  const [isBetPlacing, setIsBetPlacing] = useState(false)

  // const { data: allowanceData, refetch: refetchAllowance } = useReadContract({
  //   address: multipleCollateralContract.USDC.addresses[
  //     chain?.id as SupportedNetwork
  //   ] as `0x${string}`,
  //   abi: thalesContract.abi,
  //   functionName: 'allowance',
  //   args: [address, thalesContract.addresses[chain?.id as SupportedNetwork]],
  // })

  const { currentUser } = useUserContext()
  const { totalOdds, isOddsFetching, betAmount, isBetAllowed } =
    useDetailedOvertimeBetslip()

  const totalOddsRef = useRef(totalOdds)

  if (!isOddsFetching) {
    totalOddsRef.current = totalOdds
  }

  const {
    isApproveRequired: allowanceIsApproveRequired,
    isApprovePending: allowanceIsApprovePending,
    handleApprove,
    handleAllowanceEvent,
    allowanceData,
  } = useAllowance({
    address,
    betAmount,
    chain,
    connector,
  })

  const isApproveRequired = useMemo(() => {
    if (!betAmount) {
      return false
    }
    const allowanceNumber = Number(allowanceData || 0)
    return allowanceNumber < Number(betAmount)
  }, [allowanceData, betAmount])

  const { placeBet, totalQuote } = useBetPlacement({
    ticketMarkets,
    betAmount,
    usedCollateralForBuy:
      chain?.id && isOvertimeChain
        ? getCollateral(chain.id as SupportedNetwork, 0)
        : "USDC",
    selectedCollateralIndex: state.payment.selectedCollateralIndex,
    systemBetDenominator,
    walletClient,
    client,
    chainId: chain?.id as SupportedNetwork,
  })

  // Combine both pending states
  const isApprovePendingCombined = allowanceIsApprovePending || isBetPlacing

  // Use combined state for all logic
  useEffect(() => {
    if (isApprovePendingCombined) {
      setWasApprovePending(true)
    } else if (wasApprovePending) {
      setWasApprovePending(false)
    }
  }, [isApprovePendingCombined])

  // Handle allowance approval events
  useEffect(() => {
    if (allowanceIsApprovePending) {
      sendGTMEvent({
        event: "spend_allowance_approved",
        destination_chain: chain?.name,
        destination_wallet_address: address,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
      })
    }

    if (!allowanceIsApprovePending && wasApprovePending) {
      sendGTMEvent({
        event: "spend_allowance_rejected",
        destination_chain: chain?.name,
        destination_wallet_address: address,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
      })
    }
  }, [
    allowanceIsApprovePending,
    wasApprovePending,
    chain?.name,
    address,
    connector?.name,
  ])

  const isLoading =
    isBalanceFetching || wasApprovePending || isApprovePendingCombined
  // isMaxBetFetching ||
  // isOddsFetching ||
  // isStatesFetching ||
  // isAllowanceLoading ||
  // isPending ||
  // isProcessing ||
  // isRelayerFeeLoading

  const isDisabled =
    isLoading ||
    !address ||
    !isBetAllowed ||
    (!isEnoughBalance && !isApproveRequired) ||
    !+betAmount

  const rootClassName = cx(
    "flex items-center justify-between py-1 pr-1 border rounded-sm w-full",
    {
      "bg-accent-grey-900 border-accent-grey-950 cursor-not-allowed text-accent-grey-700":
        isDisabled,
      "bg-brand-light-500 text-black border-white/20": !isDisabled,
    }
  )
  const possibleWinClassName = cx(
    "text-caption-12 flex items-center p-2 rounded-min flex-none select-none",
    {
      "bg-accent-grey-800 text-accent-grey-700": isDisabled,
      "bg-accent-grey-1000/60 text-white": !isDisabled,
    }
  )

  // Helper function to find ticket by transaction hash or ID
  const findTicketByTxHashOrId = async (
    txHash: string
  ): Promise<string | null> => {
    try {
      const { data: userTickets } = await userTicketsQuery.refetch()

      if (!userTickets || userTickets.length === 0) {
        return null
      }

      // First try to find by transaction hash (if txHash is provided and not empty)
      if (txHash && txHash.trim() !== "") {
        const matchingTicket = userTickets.find(
          ticket => ticket.txHash === txHash
        )
        if (matchingTicket) {
          return matchingTicket.id
        }
      }

      // If not found by txHash or txHash is empty, find the most recent ticket that matches bet criteria
      const recentTickets = userTickets
        .filter(
          ticket =>
            ticket.isOpen &&
            !ticket.isResolved &&
            Math.abs(ticket.buyInAmount - Number(betAmount)) < 0.01 &&
            ticket.numOfMarkets === ticket.sportMarkets.length
        )
        .sort((a, b) => b.timestamp - a.timestamp)

      if (recentTickets.length > 0) {
        return recentTickets[0].id
      }

      return null
    } catch (error) {
      console.error("Error finding ticket:", error)
      return null
    }
  }

  const handlePlaceBet = async () => {
    try {
      if (Number(betAmount) < SYSTEM_BET_MINIMUM_MARKETS) {
        openModal("ErrorModal", {
          title: "Minimum bet amount is 3",
        })
        return
      }

      if (isApproveRequired) {
        try {
          await handleApprove()
        } catch (error) {
          // If approval fails/rejected, reset loading states
          console.error("Approval failed:", error)
          setWasApprovePending(false)
          // User will see "Approve" button again
        }
      } else {
        setIsBetPlacing(true)
        sendGTMEvent({
          event: "betslip_bet_initiated",
          user_id: currentUser?.id,
          bet_type: ticket.length > 1 ? "Combo" : "Single",
          token_approval_required: isApproveRequired,
          bet_count: ticket.length,
          bet_amount: betAmount,
          bet_payout: totalOddsRef.current * +betAmount,
          destination_chain: chain?.name,
          destination_token: getCollateral(chain?.id as SupportedNetwork, 0),
          destination_wallet_address: address,
          wallet_app_name:
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
        })

        // Track when user approves the bet transaction
        sendGTMEvent({
          event: "place_bet_approved",
          destination_chain: chain?.name,
          destination_wallet_address: address,
          wallet_app_name:
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
        })

        const txHash = await placeBet()
        const txReceipt = await waitForTransactionReceipt(client as Client, {
          hash: txHash,
        })

        if (txReceipt.status === "success") {
          sendGTMEvent({
            event: "betslip_bet_completed",
            user_id: currentUser?.id,
            bet_type: ticket.length > 1 ? "Combo" : "Single",
            token_approval_required: isApproveRequired,
            bet_count: ticket.length,
            bet_amount: betAmount,
            bet_payout: totalOddsRef.current * +betAmount,
            // freebet_amount: selectedFreeBet || 0,
            destination_chain: chain?.name,
            destination_token: getCollateral(chain?.id as SupportedNetwork, 0),
            destination_wallet_address: address,
            wallet_app_name:
              connector?.name === ConnectorName.Privy
                ? "privy"
                : connector?.name,
          })

          // Fetch user tickets and find the ticket ID by transaction hash or criteria
          const ticketId = await findTicketByTxHashOrId(
            txReceipt.transactionHash
          )

          await updateOvertimeDiscordChannel(
            ticketMarkets,
            betAmount,
            totalOddsRef.current,
            chain,
            ticketId as string,
            getCollateral(chain?.id as SupportedNetwork, 0),
            selectedCollateralSymbol,
            ticket.length > 1, // isCombo
            ticket.some(ticketPosition => ticketPosition.live) // isLive - could be determined from ticket data if needed
          )

          openModal("SuccessModal", {
            title: messages.success.title,
          })
          removeAll(() => changeBetAmount(""))
          // Refetch balances after successful bet placement
          refetchBalances()
        }
      }
    } catch (error) {
      if (!isUserRejectedRequestError(error)) {
        openModal("ErrorModal")
      }
      if (isUserRejectedRequestError(error)) {
        sendGTMEvent({
          event: "place_bet_rejected",
          destination_chain: chain?.name,
          destination_wallet_address: address,
          wallet_app_name:
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
        })
      }
      console.error("Error placing bet:", error)
    } finally {
      setIsBetPlacing(false)
    }
  }

  const analyticsData = {
    bet_type: ticket.length > 1 ? "Combo" : "Single",
    bet_amount: betAmount,
    bet_count: ticket.length,
  }

  const chainId = useChainId()
  const { payment } = useDetailedOvertimeBetslip()
  const networkId = chainId as SupportedNetwork
  const selectedCollateralSymbol = getCollateral(
    networkId,
    payment.selectedCollateralIndex
  )

  return (
    <button
      className={rootClassName}
      onClick={() => void handlePlaceBet()}
      disabled={isDisabled}
    >
      <div className="w-full text-left pl-4 px-1">
        {isLoading ? (
          <Icon className="size-4 mx-auto" name="interface/spinner" />
        ) : (
          <Message
            className="font-bold text-caption-14"
            value={
              allowanceIsApprovePending
                ? "Approving..."
                : isApproveRequired
                  ? buttonMessages.approve
                  : buttonMessages.placeBet
            }
          />
        )}
      </div>
      <div className={possibleWinClassName}>
        <Message className="mr-1" value={messages.possibleWin} />
        <div className="font-semibold">
          {toLocaleString(payout, { digits: 2 })} {selectedCollateralSymbol}
        </div>
      </div>
    </button>
  )
}

export default OvertimeBetButton
